import { X, Plus, Minus } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils/cn';

export type FilterChipMode = 'include' | 'exclude';

export interface FilterChipProps {
  /** Sichtbares Label, vom Aufrufer i18n-aufgeloest (z. B. „Service: ACME"). */
  label: string;
  /** Entfernt diesen Filterwert. */
  onRemove: () => void;
  /** ARIA-Label des Entfernen-Knopfes (vom Aufrufer i18n-aufgeloest). */
  removeLabel: string;
  /**
   * Modus „ist"/„ist nicht". Nur gesetzt, wenn der Chip einen Toggle tragen soll
   * (Mehrfach/Ausschluss-Filter, Spec 409). Ohne `mode` + `onToggleMode` rendert der
   * Chip remove-only — byte-gleich zum bisherigen Verhalten der bestehenden Filterleisten.
   */
  mode?: FilterChipMode;
  /** Schaltet zwischen include/exclude um. Nur zusammen mit `mode` wirksam. */
  onToggleMode?: () => void;
  /** ARIA-Label des Toggle-Knopfes (vom Aufrufer i18n-aufgeloest). */
  toggleLabel?: string;
  /**
   * Sichtbarer Modus-Text „ist"/„ist nicht" des aktuellen Modus (vom Aufrufer i18n-aufgeloest,
   * z. B. `t('filter.modeInclude')` / `t('filter.modeExclude')`). Wird im Toggle-Knopf neben dem
   * +/−-Icon angezeigt, damit der Modus nicht allein ueber Farbe/Icon transportiert wird.
   */
  modeLabel?: string;
  /** Maximale Label-Breite (CSS-Wert). Default `200px`. */
  maxLabelWidth?: string;
}

/**
 * Geteilter Filter-Chip (Spec 409, Foundation).
 *
 * Ein entfernbarer Aktiv-Filter-Chip. Optional mit „ist/ist nicht"-Toggle (+/−) fuer
 * Mehrfach/Ausschluss-Filter. Zuvor zweifach dupliziert (service-filter-bar,
 * platform-iq-inventory-filter-bar); hier auf eine Quelle zurueckgefuehrt.
 *
 * Rein praesentational: der Filter-Zustand lebt im konsumierenden MultiSelectFilter,
 * nicht im Chip. Strings werden vom Aufrufer injiziert (keine harten Texte hier).
 */
export function FilterChip({
  label,
  onRemove,
  removeLabel,
  mode,
  onToggleMode,
  toggleLabel,
  modeLabel,
  maxLabelWidth = '200px',
}: FilterChipProps) {
  const hasToggle = mode !== undefined && onToggleMode !== undefined;

  return (
    <Badge
      variant={mode === 'exclude' ? 'destructive' : 'outline'}
      className={cn('gap-1 pr-1', hasToggle && 'pl-1')}
    >
      {/* Spec 514: coarse-Padding statt hit-area — Toggle und Remove stehen direkt
          nebeneinander, unsichtbare Flaechen wuerden sich ueberlappen (Overlap-Regel). */}
      {hasToggle && (
        <button
          type="button"
          onClick={onToggleMode}
          aria-label={toggleLabel}
          title={toggleLabel}
          className="mr-0.5 flex touch-manipulation items-center gap-0.5 rounded-sm px-1 text-[10px] font-semibold uppercase leading-none hover:bg-muted coarse:min-h-8 coarse:px-2 coarse:py-2"
        >
          {mode === 'exclude' ? (
            <Minus className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Plus className="h-3 w-3" aria-hidden="true" />
          )}
          {modeLabel && <span>{modeLabel}</span>}
        </button>
      )}
      <span className="truncate" style={{ maxWidth: maxLabelWidth }}>
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="ml-0.5 flex touch-manipulation items-center justify-center rounded-sm hover:bg-muted coarse:min-h-8 coarse:min-w-8 coarse:p-2"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </Badge>
  );
}
